import { normalizeProductPageUrl } from '@/features/pdp/utils/pdpUrl';
import videoCallIcon from '@/shared/icons/video.svg';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useEffect, useRef, useState } from 'react';
import { CirclePlay, Loader2 } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { InStorePicksIcon, WishlistIcon } from '@/shared/icons/icons';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import ProductVideoModal from './ProductVideoModal';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { ProductCardMediaCarousel } from './ProductCardMediaCarousel';
import { RotatingProductDescription } from './RotatingProductDescription';
import discountIcon from '@/shared/icons/discount.svg';
import viewSimilarIcon from '@/shared/icons/view-similar.svg';
import viewDetailsIcon from '@/shared/icons/view-details.svg';

export interface ProductCardProps {
  /** Product/design item – use ListingDesignItem from any source (browse, search, recently-viewed) */
  product: ListingDesignItem;
  onViewSimilar?: (product: ListingDesignItem) => void;
  similarExpanded?: boolean;
  /** Called before navigating to PDP. */
  onBeforeNavigate?: (designId: number) => void;
  /** When true, open PDP in a new tab instead of the same window. */
  openInNewTab?: boolean;
  /** Analytics: called when user clicks the product card or View Details button */
  onProductClick?: (product: ListingDesignItem) => void;
  /** Analytics: eventLogger on wishlist icon tap (before API), browse PLP only in old-react */
  onWishlistClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user toggles the wishlist icon (after successful toggle) */
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
  /** Analytics: called when user clicks the video play button */
  onVideoClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user clicks the View Details button specifically */
  onViewDetailsClick?: (product: ListingDesignItem) => void;
  onVideoCallCartAdd?: (product: ListingDesignItem, errorMsg?: string) => void;
  /** When true, hides the Book Video Call button (e.g. on goldcoins PLP). */
  hideVideoCallButton?: boolean;
  /** Keep the control visible but disabled when similar-design data lacks a customization. */
  showUnavailableVideoCallButton?: boolean;
  /** When true, hides the similar-design / details action. */
  hideViewDetailsButton?: boolean;
  /** Desktop only: when true, auto-play the product video inline (every 5th card). */
  autoPlayVideo?: boolean;
  autoSlideMedia?: boolean;
  /** 1-based product position in the listing grid (eventLogger). */
  productPosition?: number;
  /** Optional custom badge rendered in the relative image container */
  badge?: React.ReactNode;
  /** Optional custom footer element (e.g. Add to Cart button) */
  footerCta?: React.ReactNode;
  /** Optional extra wrapper class name */
  className?: string;
}

export function ProductCard({
  product,
  onViewSimilar,
  similarExpanded = false,
  onBeforeNavigate,
  openInNewTab,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onViewDetailsClick,
  onVideoCallCartAdd,
  autoPlayVideo,
  autoSlideMedia = false,
  productPosition,
  hideVideoCallButton = false,
  showUnavailableVideoCallButton = false,
  hideViewDetailsButton = false,
  badge,
  footerCta,
  className,
}: ProductCardProps) {
  const [videoOpen, setVideoOpen] = useState(false);
  const { analytics } = useAnalytics();
  const trackViewSimilar = () => analytics?.trackEvent('View Similar CTA Clicked', {
    'Product Id': String(product.designId),
    'Product Name': product.designName,
    'Category Name': product.categoryType ?? '',
  });
  const [heartAnimating, setHeartAnimating] = useState(false);
  const { isWishlisted, toggleWishlist } = useWishlistToggle();
  const { addToVideoCallCart, isLoading: isVideoCallLoading } = useVideoCallCart({ showToast: false });
  const shouldAutoPlay = Boolean(autoPlayVideo && product.videoUrl);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: autoPlayRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    enabled: true,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldAutoPlay) return;
    if (isIntersecting) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isIntersecting, shouldAutoPlay]);

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartAnimating(true);
    if (productPosition != null) {
      onWishlistClick?.(product, productPosition);
    }
    const action = isWishlisted(product.designId) ? 'remove' : 'add';
    const success = await toggleWishlist(product.designId);
    if (success) {
      onWishlistToggle?.(product, action);
    }
  };

  const pdpUrl = normalizeProductPageUrl(product.productPageUrl) ?? undefined;

  const navigateToPdp = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!pdpUrl) return;
    onProductClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) {
      window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = pdpUrl;
    }
  };

  const handleViewDetailsClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!pdpUrl) return;
    onViewDetailsClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) {
      window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = pdpUrl;
    }
  };

  const wishlisted = isWishlisted(product.designId);

  const discountText = getDiscountText(product);
  const inStorePick = product.sourceType === 'POS_CART' || product.sourceType === 'POS_VIEWED';
  const hasSimilarDesigns = product.hasSimilarDesigns === true;

  return (
    <article ref={autoPlayRef} className={cn('group flex h-full flex-col overflow-hidden rounded-lg border border-[#E8E8E8] bg-white', className)}>
      <div className="relative aspect-square bg-[#F0F0F0]">
        {product.carouselMedia?.length ? (
          <ProductCardMediaCarousel designId={product.designId} designName={product.designName}
            media={product.carouselMedia} isActive={isIntersecting} autoSlide={autoSlideMedia}
            showProgress onProductClick={navigateToPdp} />
        ) : product.imageUrl ? (
          <button type="button" onClick={navigateToPdp} className="relative block h-full w-full" aria-label={product.designName}>
            <img src={product.imageUrl} alt={product.designName} className="h-full w-full object-cover" loading="lazy" />
            {product.hoverImageUrl && <img src={product.hoverImageUrl} alt="" aria-hidden
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100" loading="lazy" />}
            {shouldAutoPlay && <video ref={videoRef} src={product.videoUrl!} poster={product.imageUrl}
              muted loop playsInline className="absolute inset-0 h-full w-full object-cover" aria-hidden />}
          </button>
        ) : <div className="flex h-full items-center justify-center text-xs text-gray-400">No image</div>}

        <button type="button" aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="absolute left-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#777]"
          onClick={handleWishlistClick}>
          <WishlistIcon filled={wishlisted} className={cn('h-6 w-6', wishlisted && 'text-[#B98599]', heartAnimating && 'animate-heart-pop')}
            aria-hidden onAnimationEnd={() => setHeartAnimating(false)} />
        </button>
        {product.videoUrl && (
          <button type="button" aria-label="Play product video"
            className="absolute right-3 top-14 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#777]"
            onClick={event => {
              event.stopPropagation();
              if (productPosition != null) onVideoClick?.(product, productPosition);
              setVideoOpen(true);
            }}>
            <CirclePlay className="h-6 w-6" strokeWidth={1.5} aria-hidden />
          </button>
        )}
        {(product.customizationId != null || showUnavailableVideoCallButton) && !hideVideoCallButton && (
          <button type="button" aria-label="Add to video call cart" disabled={isVideoCallLoading || product.customizationId == null}
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#16823D] disabled:opacity-50"
            onClick={async event => {
              event.stopPropagation();
              const result = await addToVideoCallCart(product.customizationId!);
              onVideoCallCartAdd?.(product, result.success ? undefined : result.message);
            }}>
            {isVideoCallLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <img src={videoCallIcon} alt="" aria-hidden="true" className="h-6 w-6" />}
          </button>
        )}
        {!hideViewDetailsButton && (
          <button type="button" aria-label={hasSimilarDesigns ? 'View similar designs' : 'View details'}
            aria-expanded={hasSimilarDesigns ? similarExpanded : undefined}
            aria-controls={hasSimilarDesigns && similarExpanded ? `similar-designs-${product.designId}` : undefined}
            className={cn("absolute bottom-4 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full", similarExpanded ? "bg-[#103064]" : "bg-white")}
            onClick={event => {
              if (hasSimilarDesigns) {
                event.stopPropagation();
                if (!similarExpanded) trackViewSimilar();
                onViewSimilar?.(product);
              }
              else handleViewDetailsClick(event);
            }}>
            <img src={hasSimilarDesigns ? viewSimilarIcon : viewDetailsIcon} alt="" className={cn("h-6 w-6", similarExpanded ? "brightness-0 invert" : "grayscale opacity-70")} />
          </button>
        )}
        {inStorePick ? (
          <div className="absolute bottom-1 left-0 z-10 flex items-center gap-2 rounded-r-lg px-3 py-2 text-sm font-semibold tracking-wide text-[#103064]"
            style={{ background: 'linear-gradient(90deg, #E5F4EF 0%, #EFF4DE 25.48%, #EFEAEE 49.52%, #DDF5F9 91.83%)' }}>
            <InStorePicksIcon className="h-4 w-4 shrink-0" />Your In-Store Picks
          </div>
        ) : badge}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 border-t border-[#E8E8E8] px-3 py-3">
        <h2 className="text-[15px] font-semibold leading-6 tracking-wide text-[#595959]">
          <a href={pdpUrl} onClick={event => { event.preventDefault(); navigateToPdp(); }}>{product.designName}</a>
        </h2>
        <p className="text-[14px] font-bold leading-6 text-[#103064]">{product.discountedPrice || product.price}</p>
        <RotatingProductDescription product={product} className="flex h-5 min-w-0 items-center overflow-hidden text-sm leading-5 text-[#595959]" />
        {discountText && <p className="mt-1 flex items-start gap-2 bg-[#E9FFF0] px-2 py-1 text-sm leading-5 text-[#16823D]">
          <img src={discountIcon} alt="" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{discountText}</span>
        </p>}
        {footerCta && <div className="mt-auto pt-1">{footerCta}</div>}
      </div>
      {product.videoUrl && <ProductVideoModal open={videoOpen} onClose={() => setVideoOpen(false)}
        videoUrl={product.videoUrl} videoPosterUrl={product.videoPosterUrl}
        productName={product.designName} productPageUrl={product.productPageUrl} />}
    </article>
  );
}

function getDiscountText(
  product: Pick<
    ListingDesignItem,
    'discountName' | 'discountValue' | 'discountPercent'
  >
): string {
  // If API provides a custom offer label (e.g. "70% off on Making Charges"), use it directly
  if (product.discountName) {
    return product.discountName;
  }

  // Fallback: "Save ₹X" style text when there is a numeric discount but no named offer
  if (product.discountPercent && product.discountPercent > 0 && product.discountValue) {
    return `Save ${product.discountValue}`;
  }

  return '';
}
